import { Box, IconButton } from '@mui/material';
import { type GridRowId, type GridTreeNode, isAutogeneratedRow, useGridApiContext } from '@mui/x-data-grid-premium';
import { RiEyeLine, RiPencilLine } from '@remixicon/react';
import type { ReactNode } from 'react';
import Hint from 'src/components/atoms/Hint';
import type { GridRow } from 'src/lib/gridRows.store';

import type { LotTransferItemPalletTags, LotTransferItemRow } from './InventoryTransferGrid';

/** The row as the cell reads it; `id` is a string only on a legacy auto-generated nested parent. */
type PalletTagsCellRow = Omit<GridRow<LotTransferItemRow>, 'id'> & { id?: GridRowId; isNew?: boolean };

type InventoryTransferPalletTagsCellProps = {
  row: PalletTagsCellRow;
  rowNode: GridTreeNode;
  handleOpenTagsModal?: (rowId: GridRowId, isNew: boolean | undefined, nestedParentId: GridRowId | undefined) => void;
  canEdit?: boolean;
  isEditing?: boolean;
  getRowPalletTags: (row: PalletTagsCellRow) => LotTransferItemPalletTags | null | undefined;
  palletTagsColumn?: string;
};

export const InventoryTransferPalletTagsCell = ({
  row: savedRow,
  rowNode,
  handleOpenTagsModal,
  canEdit,
  isEditing = false,
  getRowPalletTags,
  palletTagsColumn = 'palletTags',
}: InventoryTransferPalletTagsCellProps): ReactNode => {
  const apiRef = useGridApiContext();
  const row = isEditing
    ? (apiRef.current.getRowWithUpdatedValues(rowNode.id, palletTagsColumn) as PalletTagsCellRow)
    : savedRow;

  if (isAutogeneratedRow(row) || !row.lot) {
    return undefined;
  }

  const palletTags = getRowPalletTags(row);
  const tagNumbers = palletTags
    ?.flatMap((tag) => (tag && tag.quantity !== 0 ? [tag.palletTag.tagNumber] : []))
    .join(', ');

  if (rowNode.type === 'group' && (rowNode.children.length > 1 || rowNode.childrenExpanded)) {
    return '';
  }

  // work around to allow auto generated nested parent to properly select pallet tags
  // TODO: Remove when auto generated parent rows are no longer necessary
  const isNestedParent = typeof row.id === 'string' && row.id.startsWith('auto-generated-nested-parent');
  // `rowNode.id` IS the grid's row id — the store rowId on an EditableDataGrid
  // surface (ruling B1), and identical to `row.id` on the legacy stack where
  // `getRowId = row => row.id`. Everything downstream addresses rows this way.
  const rowId = isNestedParent ? (rowNode.parent ?? rowNode.id) : rowNode.id;

  const openTagModal = () => {
    handleOpenTagsModal?.(rowId, row.isNew, isNestedParent ? row.id : undefined);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
      <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mr: 1, flexGrow: 1 }}>
        {tagNumbers}
      </Box>
      {canEdit ? (
        <Hint title="Edit pallet tags to transfer">
          <IconButton disabled={!row.lot} size="small" sx={{ ml: 'auto' }} onClick={openTagModal}>
            <RiPencilLine size={18} />
          </IconButton>
        </Hint>
      ) : (
        !!palletTags?.length && (
          <Hint title="View selected pallet tags">
            <IconButton disabled={!row.lot} size="small" sx={{ ml: 'auto' }} onClick={openTagModal}>
              <RiEyeLine size={18} />
            </IconButton>
          </Hint>
        )
      )}
    </Box>
  );
};
